import type {
  CompatibilityAccessoryInput,
  FullProductData
} from 'utils/compatibility-systems';
import type { SavedQuotableSystemRecord } from 'utils/compatibility-quotable-types';
import type { SelectedCombination } from 'utils/quotable-add-pipeline';

export type ResolvedCombinationProducts = {
  replacingProductIds: Set<number>;
  outdoorProduct: FullProductData | null;
  indoorProduct: FullProductData | null;
  blowerProduct: FullProductData | null;
  accessories: CompatibilityAccessoryInput[];
};

function slotProduct(
  slot: { productId: number } | null,
  enrichedProducts: Record<number, FullProductData>
): FullProductData | null {
  return slot ? enrichedProducts[slot.productId] ?? null : null;
}

export function resolveQuotableAccessories(
  quotableSystem: SavedQuotableSystemRecord
): CompatibilityAccessoryInput[] {
  return quotableSystem.products
    .filter((product) => product.productTypeCode === 'accessory')
    .map((product) => {
      const enriched = quotableSystem.enrichedProducts[product.productId];
      if (!enriched) return null;
      return {
        productId: enriched.productId,
        name: enriched.name,
        description: enriched.description,
        price: enriched.price ?? 0,
        accessoryTypeCode: product.accessoryTypeCode ?? 'accessory',
        productUrl: enriched.productUrl ?? '',
        photo: enriched.photo ?? '',
        fulfillableStock: enriched.fulfillableStock
      };
    })
    .filter(
      (accessory): accessory is NonNullable<typeof accessory> =>
        accessory !== null
    );
}

export function resolveCombinationProducts(
  combo: SelectedCombination,
  quotableSystem: SavedQuotableSystemRecord
): ResolvedCombinationProducts {
  const replacingProductIds = new Set<number>();
  if (combo.outdoor) replacingProductIds.add(combo.outdoor.productId);
  if (combo.indoor) replacingProductIds.add(combo.indoor.productId);
  if (combo.blower) replacingProductIds.add(combo.blower.productId);
  return {
    replacingProductIds,
    outdoorProduct: slotProduct(combo.outdoor, quotableSystem.enrichedProducts),
    indoorProduct: slotProduct(combo.indoor, quotableSystem.enrichedProducts),
    blowerProduct: slotProduct(combo.blower, quotableSystem.enrichedProducts),
    accessories: resolveQuotableAccessories(quotableSystem)
  };
}

export function toCombinationPayload(combo: SelectedCombination) {
  return {
    outdoor: combo.outdoor,
    indoor: combo.indoor,
    blower: combo.blower,
    seer2: combo.seer2,
    eer2: combo.eer2,
    hspf2: combo.hspf2,
    cop5: combo.cop5,
    coolingCapacity: combo.coolingCapacity,
    heatingCapacity: combo.heatingCapacity,
    afue: combo.afue,
    systemTypeLabel: combo.systemTypeLabel,
    accessories: combo.accessories,
    designLoadHeatingCoveragePercent:
      combo.designCoverage?.heatingCoveragePercent ?? null,
    combinationId: combo.combinationId
  };
}
